import { Download, ExternalLink, FileText, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { apiBlob, apiPost } from "@/services/studio/client";
import { IMPROVE_HANDOFF_URL, writeImproveHandoff } from "@/hooks/multi-agent";
import { artifactUrl } from "@/lib/studio/runs";
import { formatBytes } from "@/lib/studio/utils";
import {
  artifactValidationOutcome,
  asValidationOutcome,
  unvalidatedReasonLabel,
  validationCountsSummary,
  validationPresentation,
} from "@/lib/studio/validation";
import type { RunArtifact, ValidationOutcome } from "@/services/studio/types";
import { WeaveIcon } from "@/components/atoms/WeaveIcon";

interface ArtifactListProps {
  runId: string;
  artifacts: RunArtifact[];
  variant?: "rows" | "chips";
  orgId?: string | null;
}

export interface ValidateResult {
  success: boolean;
  errors?: string[];
  /** "validated" | "validation_failed" | "unvalidated" — never "deployed". */
  status?: string;
  /** Same literal as `status`; the backend sends both. */
  outcome?: string;
  /** Why an `unvalidated` check never completed, e.g. "poll_timeout". */
  unvalidated_reason?: string | null;
  message?: string;
  fix_attempts?: number;
  fixed_content?: string | null;
  fixed_files?: Record<string, string> | null;
  artifact_revisions?: Array<{
    id: string;
    name: string;
    revision: number;
    previous_revision: number;
    superseded_id: string;
  }>;
}

/**
 * The verdict to show for one artifact: the result of a validation just triggered from
 * this list, else the verdict persisted on the artifact from the run itself. `success`
 * is deliberately NOT consulted — it is false for both `validation_failed` and
 * `unvalidated`, which is exactly the conflation this screen has to stop making.
 *
 * Exported for the migrations-engine surface, which has no "validate now" affordance and
 * so always passes `undefined` for `live` — it reads the persisted verdict through this
 * very function rather than growing a second, divergent read of the same three outcomes.
 */
export function verdictFor(
  artifact: RunArtifact,
  live: ValidateResult | undefined
): { outcome: ValidationOutcome; errors: string[]; reason: string | null } | null {
  if (live) {
    const outcome = asValidationOutcome(live.outcome ?? live.status);
    if (outcome) {
      return {
        outcome,
        errors: live.errors ?? [],
        reason: live.unvalidated_reason ?? null,
      };
    }
    // A transport/HTTP error never reached the org — that is "we could not check",
    // not "your file is broken".
    return { outcome: "unvalidated", errors: live.errors ?? [], reason: null };
  }
  const outcome = artifactValidationOutcome(artifact.validation);
  if (!outcome) return null;
  return {
    outcome,
    errors: artifact.validation?.errors ?? [],
    reason: artifact.validation?.reason ?? null,
  };
}

export function ArtifactList({
  runId,
  artifacts,
  variant = "rows",
  orgId,
}: ArtifactListProps) {
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [chatImportName, setChatImportName] = useState<string | null>(null);
  const [validatingName, setValidatingName] = useState<string | null>(null);
  const [validateResults, setValidateResults] = useState<Record<string, ValidateResult>>({});
  const navigate = useNavigate();
  const orderedArtifacts = orderArtifacts(artifacts);

  const artifactPath = (artifact: RunArtifact) => artifactUrl(runId, artifact.name, orgId);

  const downloadArtifact = async (artifact: RunArtifact) => {
    setDownloadError(null);
    try {
      const { blob, filename } = await apiBlob(artifactPath(artifact));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || artifact.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Download failed";
      setDownloadError(`${artifact.name}: ${message}`);
    }
  };

  // Open in a new tab WITH auth — mirrors AgentArtifactList. A raw <a href target=_blank>
  // 404s because a browser navigation can't send the X-User-ID / org auth header the
  // artifact endpoint requires (org-scoped run ownership). So open a blank tab
  // synchronously (popup-blocker safe), fetch WITH auth, then point the tab at the blob.
  const openArtifact = async (artifact: RunArtifact) => {
    setDownloadError(null);
    const win = window.open("about:blank", "_blank");
    try {
      const { blob } = await apiBlob(artifactPath(artifact));
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      if (win) win.close();
      const message = err instanceof Error ? err.message : "Could not open artifact";
      setDownloadError(`${artifact.name}: ${message}`);
    }
  };

  const validateArtifact = async (artifact: RunArtifact) => {
    setValidatingName(artifact.name);
    setValidateResults((prev) => {
      const next = { ...prev };
      delete next[artifact.name];
      return next;
    });
    try {
      const result = await apiPost<ValidateResult>(
        `/api/playbook-runs/${runId}/artifacts/${encodeURIComponent(artifact.name)}/deploy`,
        {}
      );
      setValidateResults((prev) => ({ ...prev, [artifact.name]: result }));
      const outcome = asValidationOutcome(result.outcome ?? result.status);
      if (outcome === "validated") {
        const autoFixed = result.fix_attempts && result.fix_attempts > 1;
        toast.success(autoFixed ? `Validated — auto-fixed in ${result.fix_attempts} attempt(s)` : "Validated successfully");
      } else if (outcome === "validation_failed") {
        toast.error("Validation failed — see details below");
      } else {
        // "unvalidated": the check never completed. Not an error toast — nothing is
        // known to be wrong with the file, we just did not manage to check it.
        toast(result.message || "Could not validate — the check did not complete.", { icon: "⚠️" });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not reach the validation service";
      // The request never got a verdict back, so record it as unvalidated rather than
      // as a validation failure the org never actually reported.
      setValidateResults((prev) => ({
        ...prev,
        [artifact.name]: { success: false, status: "unvalidated", errors: [message] },
      }));
      toast(message, { icon: "⚠️" });
    } finally {
      setValidatingName(null);
    }
  };

  // Send a single artifact into MULTI-AGENT (the product's "not satisfied → refine"
  // path), NOT dev-agent — mirrors AgentArtifactList. The old dev-agent import hit
  // a nonexistent POST /api/artifacts/session/{uuid}/import (404), and its axios 401
  // interceptor could force a logout. Fetch the artifact WITH auth and hand it to
  // multi-agent via the shared improve handoff.
  const sendToChat = async (artifact: RunArtifact) => {
    setDownloadError(null);
    setChatImportName(artifact.name);
    try {
      let content = "";
      try {
        const { blob } = await apiBlob(artifactPath(artifact));
        content = (await blob.text()).slice(0, 15000);
      } catch { /* still hand off the reference if the content fetch fails */ }
      writeImproveHandoff({
        source: "migration",
        runId,
        artifacts: content ? [{ name: artifact.name, content }] : [],
      });
      navigate(IMPROVE_HANDOFF_URL);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not send artifact to chat";
      setDownloadError(`${artifact.name}: ${message}`);
      toast.error(message);
    } finally {
      setChatImportName(null);
    }
  };

  if (!artifacts || artifacts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No downloads are available for this run yet.
      </p>
    );
  }

  if (variant === "chips") {
    return (
      <>
        <div className="flex flex-wrap gap-2">
          {orderedArtifacts.map((a) => (
            <span key={a.name} className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={() => void downloadArtifact(a)}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-mono text-xs hover:bg-muted cursor-pointer",
                  isPrimaryRunArtifact(a)
                    ? "border-[hsl(var(--accent-warm))]/55 bg-[hsl(var(--accent-warm))]/10"
                    : "border-border bg-card",
                ].join(" ")}
                title={`${a.content_type} · download`}
              >
                <FileText className="h-3.5 w-3.5 text-[hsl(var(--accent-warm))]" />
                <span className="truncate max-w-[12rem]">{a.name}</span>
                {isPrimaryRunArtifact(a) ? (
                  <span className="rounded bg-[hsl(var(--accent-warm))]/15 px-1.5 py-0.5 text-[10px] uppercase text-[hsl(var(--accent-warm))]">
                    full
                  </span>
                ) : null}
                <span className="text-muted-foreground">
                  ({formatBytes(a.size_bytes)})
                </span>
                <RevisionTag revision={a.revision} />
                <Download className="h-3 w-3 opacity-70" />
              </button>
              <Button
                variant="secondary"
                size="icon"
                title="Weave in"
                disabled={chatImportName === a.name}
                onClick={() => void sendToChat(a)}
              >
                <WeaveIcon className="h-3.5 w-3.5" />
              </Button>
              {isDeployable(a.name) ? (
                <Button
                  variant="secondary"
                  size="icon"
                  title="Validate against org"
                  disabled={validatingName === a.name}
                  onClick={() => void validateArtifact(a)}
                >
                  <ShieldCheck className={`h-3.5 w-3.5 ${validatingName === a.name ? "animate-pulse" : ""}`} />
                </Button>
              ) : null}
              <ValidationBadge verdict={verdictFor(a, validateResults[a.name])} deployable={isDeployable(a.name)} />
            </span>
          ))}
        </div>
        <ValidationDetails artifacts={orderedArtifacts} results={validateResults} />
        {downloadError ? (
          <p className="mt-2 text-xs text-destructive">{downloadError}</p>
        ) : null}
      </>
    );
  }

  return (
    <>
      <ul className="divide-y divide-border">
        {orderedArtifacts.map((a) => (
          <li
            key={a.name}
            className="flex flex-wrap items-center justify-between gap-3 py-2"
          >
            <div className="flex min-w-0 items-center gap-2">
              <FileText className="h-4 w-4 shrink-0 text-[hsl(var(--accent-warm))]" />
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="font-mono text-sm truncate">{a.name}</div>
                  {isPrimaryRunArtifact(a) ? (
                    <span className="shrink-0 rounded border border-[hsl(var(--accent-warm))]/45 px-1.5 py-0.5 font-mono text-[10px] uppercase text-[hsl(var(--accent-warm))]">
                      full package
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                  <span>
                    {a.content_type} · {formatBytes(a.size_bytes)}
                  </span>
                  <RevisionTag revision={a.revision} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={chatImportName === a.name}
                onClick={() => void sendToChat(a)}
              >
                <WeaveIcon className="h-3.5 w-3.5" />
                Let's weave
              </Button>
              <Button variant="ghost" size="sm" onClick={() => void openArtifact(a)}>
                <ExternalLink className="h-3.5 w-3.5" />
                Open
              </Button>
              <Button variant="outline" size="sm" onClick={() => void downloadArtifact(a)}>
                <Download className="h-3.5 w-3.5" /> Download
              </Button>
              {isDeployable(a.name) ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={validatingName === a.name}
                  onClick={() => void validateArtifact(a)}
                >
                  <ShieldCheck className={`h-3.5 w-3.5 ${validatingName === a.name ? "animate-pulse" : ""}`} />
                  {validatingName === a.name ? "Validating…" : "Validate Only"}
                </Button>
              ) : null}
              <ValidationBadge verdict={verdictFor(a, validateResults[a.name])} deployable={isDeployable(a.name)} />
            </div>
          </li>
        ))}
      </ul>
      <ValidationDetails artifacts={orderedArtifacts} results={validateResults} />
      {downloadError ? (
        <p className="mt-2 text-xs text-destructive">{downloadError}</p>
      ) : null}
    </>
  );
}

/**
 * The artifact's revision number, when the backend recorded one.
 *
 * Artifacts are revisioned IN PLACE: a validation auto-fix rewrites the stored file and
 * bumps `revision` (see `artifact_revisions` on ValidateResult). Until now that number
 * was normalized off the wire and then never shown, so "the file the run produced" and
 * "the file that was rewritten twice afterwards to make the org accept it" looked
 * identical in the list. Anything past rev 1 is called out rather than whispered.
 */
function RevisionTag({ revision }: { revision?: number }) {
  if (typeof revision !== "number" || !Number.isFinite(revision) || revision < 1) return null;
  const rewritten = revision > 1;
  return (
    <span
      className={[
        "shrink-0 rounded border px-1 py-0.5 font-mono text-[10px] uppercase leading-none",
        rewritten
          ? "border-[hsl(var(--accent-warm))]/45 text-[hsl(var(--accent-warm))]"
          : "border-border text-muted-foreground",
      ].join(" ")}
      title={
        rewritten
          ? `Revision ${revision} — this file was rewritten ${revision - 1} time(s) after the run first produced it. Earlier revisions are superseded.`
          : "Revision 1 — this is the file the run originally produced."
      }
    >
      rev {revision}
    </span>
  );
}

/**
 * Three-way verdict chip. `unvalidated` is deliberately neither green nor red — it
 * carries the caution "warm" token, because the org never told us anything about this
 * file one way or the other.
 *
 * Exported so surfaces other than the playbook artifact list (the migrations engine
 * task report) present an identical verdict rather than growing a second, divergent
 * reading of the same three outcomes.
 */
export function ValidationBadge({
  verdict,
  deployable,
}: {
  verdict: { outcome: ValidationOutcome; errors: string[]; reason: string | null } | null;
  /** Whether the org could validate this file at all. Reports and CSVs cannot. */
  deployable: boolean;
}) {
  // No verdict on a file the org COULD have validated — runs that predate the
  // validation field, which is most existing data. Silence there is ambiguous: it
  // looks the same as a report that was never eligible. Say so neutrally instead.
  if (!verdict) {
    if (!deployable) return null;
    return (
      <Badge
        variant="secondary"
        className="gap-1"
        title="No validation verdict was recorded for this file. It has not been checked against the org."
      >
        <ShieldQuestion className="h-3 w-3" />
        Not checked
      </Badge>
    );
  }
  const { tone, label, detail } = validationPresentation(verdict.outcome);
  const Icon =
    verdict.outcome === "validated"
      ? ShieldCheck
      : verdict.outcome === "validation_failed"
        ? ShieldAlert
        : ShieldQuestion;
  return (
    <Badge variant={tone} className="gap-1" title={detail}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

/**
 * The WHY line under the list, preceded by a roll-up. An `unvalidated` artifact shows
 * the reason the check did not complete (not an error), a failed one shows the org's
 * actual errors.
 */
function ValidationDetails({
  artifacts,
  results,
}: {
  artifacts: RunArtifact[];
  results: Record<string, ValidateResult>;
}) {
  const verdicts = artifacts
    .map((artifact) => ({ artifact, verdict: verdictFor(artifact, results[artifact.name]) }))
    .filter((row) => row.verdict !== null);
  const counts = verdicts.reduce(
    (acc, { verdict }) => ({ ...acc, [verdict!.outcome]: acc[verdict!.outcome] + 1 }),
    { validated: 0, validation_failed: 0, unvalidated: 0 }
  );
  const summary = validationCountsSummary(counts);
  const rows = verdicts.filter((row) => row.verdict!.outcome !== "validated");
  if (rows.length === 0 && !summary) return null;
  return (
    <div className="mt-2 space-y-1">
      {summary ? (
        <p className="font-mono text-[11px] uppercase text-muted-foreground">{summary}</p>
      ) : null}
      {rows.map(({ artifact, verdict }) => {
        const failed = verdict!.outcome === "validation_failed";
        const why = failed
          ? verdict!.errors.join("; ")
          : unvalidatedReasonLabel(verdict!.reason) ?? validationPresentation("unvalidated").detail;
        return (
          <p
            key={artifact.name}
            className={`text-xs ${failed ? "text-destructive" : "text-muted-foreground"}`}
          >
            <span className="font-mono">{artifact.name}</span>: {why}
          </p>
        );
      })}
    </div>
  );
}

function orderArtifacts(artifacts: RunArtifact[]): RunArtifact[] {
  return [...artifacts].sort((left, right) => artifactRank(left) - artifactRank(right) || left.name.localeCompare(right.name));
}

function artifactRank(artifact: RunArtifact): number {
  const name = artifact.name.toLowerCase();
  if (isPrimaryRunArtifact(artifact)) return 0;
  if (name.startsWith("apex_call_graph")) return 0;
  if (name.startsWith("dependency_cycle") || name.startsWith("dependency_cycles")) return 0;
  if (name.endsWith(".md") && !name.includes("_prompt") && !name.includes("_full_source")) return 1;
  if (name.endsWith(".html")) return 2;
  if (name.endsWith(".pdf")) return 3;
  if (name.includes("_full_source")) return 4;
  if (name.includes("_full_run_metadata")) return 5;
  if (name.includes("_evidence") || name.includes("_share_context") || name.includes("_action_manifest")) return 6;
  if (name.includes("_steps") || name.includes("_findings") || name.includes("_prompt")) return 7;
  if (name.endsWith(".json") || name.endsWith(".csv")) return 8;
  return 10;
}

function isDeployable(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    lower.endsWith(".flow-meta.xml") ||
    lower.endsWith(".cls") ||
    lower.endsWith(".trigger") ||
    lower.endsWith(".permissionset-meta.xml") ||
    lower.endsWith(".namedcredential-meta.xml") ||
    lower.endsWith(".js-meta.xml")
  );
}

function isPrimaryRunArtifact(artifact: RunArtifact): boolean {
  const name = artifact.name.toLowerCase();
  if (name.includes("_source_") || name.includes("_full_source") || name.includes("_full_run_metadata")) return false;
  if (name.includes("_evidence") || name.includes("_share_context") || name.includes("_action_manifest")) return false;
  if (name.includes("_steps") || name.includes("_findings") || name.includes("_prompt")) return false;
  if (/^apex_call_graph(?:_analysis|_cycles)?\.(md|csv|json|mmd)$/.test(name)) return true;
  if (/^dependency_cycles?\.(md|csv|json|mmd)$/.test(name)) return true;
  if (/^dependency_cycle_analysis\.md$/.test(name)) return true;
  if (/(?:notes|generation_notes|validation_notes)\.md$/.test(name)) return true;
  return /\.(cls|trigger|soql|apex|xml|js|html|css)$/.test(name);
}
